import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { AdminCertificatePlatform } from '../types/models/AdminCertificatePlatform';
import type { ApiResponseAdminCertificatePlatformList } from '../types/api/ApiResponseAdminCertificatePlatform';

interface UseAdminCertificatePlatformsReturn {
  platforms: AdminCertificatePlatform[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Upsert one platform row from a create/update mutation response (matched by `id`), so a save
   * updates its row without a full refetch (which would flash the loading spinner over the table).
   * A row that isn't in the list yet (a fresh create) is appended — the backend orders by id, so
   * appending the newest row preserves that order.
   */
  applyPlatform: (updated: AdminCertificatePlatform) => void;
  /** Drop one platform row after a successful DELETE. */
  removePlatform: (id: number) => void;
}

// GET /admin/certificate-platforms lists the whole catalog — shared rows and rows dedicated to any
// tenant — ordered by id. Staff-only on the backend; the page mirrors the gate. useApiFetch injects
// the bearer token (and, irrelevantly here, the impersonation header — these admin routes authorize
// on the real staff user).
//
// `enabled` (default true) defers the fetch so a non-staff user who lands on the route directly
// (and is shown <NotFound />) never fires the backend-rejected admin call: the page passes its own
// `is_ancalagon_staff` check in, matching the page's "reveal nothing" defense-in-depth.
export const useAdminCertificatePlatforms = (enabled = true): UseAdminCertificatePlatformsReturn => {
  const [platforms, setPlatforms] = useState<AdminCertificatePlatform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchPlatforms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/admin/certificate-platforms');
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminCertificatePlatformList | null;
      if (body?.status === 'success' && body.data?.certificate_platforms) {
        setPlatforms(body.data.certificate_platforms);
      } else {
        setError(body?.message ?? 'Failed to load certificate platforms');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching certificate platforms');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const applyPlatform = useCallback((updated: AdminCertificatePlatform) => {
    setPlatforms((prev) =>
      prev.some((p) => p.id === updated.id)
        ? prev.map((p) => (p.id === updated.id ? updated : p))
        : [...prev, updated],
    );
  }, []);

  const removePlatform = useCallback((id: number) => {
    setPlatforms((prev) => prev.filter((p) => p.id !== id));
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchPlatforms();
    }
  }, [enabled, auth.isAuthenticated, fetchPlatforms]);

  return { platforms, loading, error, refetch: fetchPlatforms, applyPlatform, removePlatform };
};
